import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Spinner,
} from "@chakra-ui/react";
import { GoogleMap, PolygonF } from "@react-google-maps/api";
import type { Geometry, Position } from "geojson";
import _ from "lodash";
import React, { useEffect, useState } from "react";
import { positionToPath } from "src/components/Boundary/geoJSON";
import { useLoadGooglePlacesScript } from "src/hooks/useLoadGoogleMaps";
import * as R from "src/types/result";

interface Boundary {
  geometry: Geometry;
  name?: string;
}
export type Props = { boundaries: Boundary[] };
export const ViewSchoolBoundaries: React.FC<Props> = ({ boundaries }) => {
  const { isLoaded } = useLoadGooglePlacesScript();
  const [displayBoundaries, setDisplayBoundaries] = useState<Boundary[]>([]);

  useEffect(() => {
    if (boundaries.length > 0) {
      setDisplayBoundaries(
        _.uniqBy(boundaries, (x) => x.name ?? "").filter((x) => x.name)
      );
    }
  }, [boundaries]);
  return (
    <Accordion allowToggle>
      <AccordionItem>
        {({ isExpanded }) => (
          <>
            <AccordionButton>
              <Box fontSize="xs" color="gray.800" flex="1" textAlign="left">
                Boundaries
              </Box>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel>
              {isExpanded && !isLoaded && (
                <Spinner color="primary.500" size="lg" />
              )}

              {isExpanded && isLoaded && displayBoundaries.length > 0 && (
                <GoogleMap
                  mapContainerStyle={{
                    width: "100%",
                    height: "500px",
                  }}
                  zoom={10}
                  onLoad={(map) => {
                    const bounds = calculateBounds(
                      displayBoundaries.map((b) => b.geometry)
                    );
                    map.fitBounds(bounds);
                  }}
                >
                  {displayBoundaries.map((boundary, index) => {
                    const result = geometryToPath(boundary.geometry);
                    if (!result.success) {
                      console.error(result.error);
                      return null;
                    }
                    return result.data.map((path, ringIndex) => (
                      <PolygonF path={path} key={`${index}-${ringIndex}`} />
                    ));
                  })}
                </GoogleMap>
              )}
            </AccordionPanel>
          </>
        )}
      </AccordionItem>
    </Accordion>
  );
};

function calculateBounds(geometries: Geometry[]): google.maps.LatLngBounds {
  const bounds = new google.maps.LatLngBounds();

  geometries.forEach((geometry) => {
    const result = geometryToPath(geometry);
    if (!result.success) {
      console.error(result.error);
      return;
    }
    result.data.forEach((path) =>
      path.forEach((point) => bounds.extend(point))
    );
  });

  return bounds;
}

function ringToPath(ring: Position[]): R.Result<google.maps.LatLngLiteral[]> {
  return R.join(ring.map(positionToPath)).map((positions) =>
    positions.map(([lng, lat]) => ({ lat, lng }))
  );
}

function geometryToPath(
  geometry: Geometry
): R.Result<google.maps.LatLngLiteral[][]> {
  switch (geometry.type) {
    case "Polygon": {
      const exteriorRing = geometry.coordinates[0];
      if (exteriorRing === undefined) {
        return R.failure("Invalid polygon");
      }
      return ringToPath(exteriorRing).map((path) => [path]);
    }
    case "MultiPolygon": {
      if (geometry.coordinates.length === 0) {
        return R.failure("Invalid multipolygon");
      }
      return R.join(
        geometry.coordinates.map(
          (polygon): R.Result<google.maps.LatLngLiteral[]> => {
            const exteriorRing = polygon[0];
            if (exteriorRing === undefined) {
              return R.failure("Invalid multipolygon");
            }
            return ringToPath(exteriorRing);
          }
        )
      );
    }
    default:
      return R.failure(`Unsupported geometry type: ${geometry.type}`);
  }
}
